/**
 * useUploader — uploads captured frames to Azure Blob via SAS URLs.
 *
 * Handles: batch SAS URL fetching, parallel uploads (4 concurrent),
 * progress tracking, retry logic, scan completion, and resume after interruption.
 *
 * Frames are persisted in IndexedDB by useCamera. This hook tracks upload progress
 * there so interrupted uploads can be resumed after browser kill / phone call.
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { createScan, getUploadUrls, uploadFrame, markScanComplete, getScan } from '@services/scanApi'
import {
  getFramesBySession,
  getPendingFrames,
  updateSession,
  deleteSessionAndFrames,
} from '@services/frameStore'
import type { ScanSession, PersistedFrame } from '@services/frameStore'
import { apiUrl } from '../config'
import { getAuthHeaders } from '../stores/authStore'
import type { Scan, UploadUrl } from '@app-types/scan'

interface FramePair {
  blob: Blob     // Low-res for COLMAP/navigation
  hdBlob: Blob   // HD for zoom display
  ultraBlob?: Blob // 4K for deep zoom (only when ultra tier)
}

interface UploadProgress {
  total: number
  uploaded: number
  failed: number
  percent: number
}

type UploadPhase = 'idle' | 'creating' | 'uploading' | 'completing' | 'polling' | 'done' | 'error'

const CONCURRENT_UPLOADS = 4
const POLL_INTERVAL_MS = 5000
const MAX_RETRIES = 5

export function useUploader() {
  const [phase, setPhase] = useState<UploadPhase>('idle')
  const [progress, setProgress] = useState<UploadProgress>({ total: 0, uploaded: 0, failed: 0, percent: 0 })
  const [scan, setScan] = useState<Scan | null>(null)
  const [error, setError] = useState<string | null>(null)
  const pollRef = useRef<number | null>(null)
  const isUploadingRef = useRef(false)
  const abortedRef = useRef(false)

  // Warn user before closing browser during upload
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isUploadingRef.current) {
        e.preventDefault()
        e.returnValue = 'Upload in progress. Are you sure you want to leave?'
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  // Auto-restart stalled uploads when page becomes visible again (e.g. after phone call)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isUploadingRef.current && abortedRef.current) {
        console.log('[useUploader] Page became visible, upload was stalled — will retry on next batch')
        abortedRef.current = false
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  // Stop status polling
  const stopPolling = useCallback(() => {
    if (pollRef.current !== null) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }, [])

  // Start polling for processing completion
  const startPolling = useCallback(
    (scanId: string, shopId: string, location?: { lat: number; lng: number } | null) => {
      setPhase('polling')
      pollRef.current = window.setInterval(async () => {
        try {
          const updated = await getScan(scanId)
          setScan(updated)
          if (updated.status === 'ready' || updated.status === 'failed') {
            stopPolling()
            setPhase('done')

            // Auto-update shop location if null (only on successful processing)
            if (updated.status === 'ready' && location) {
              try {
                const shopRes = await fetch(apiUrl(`/api/shops/${shopId}`), { headers: getAuthHeaders() })
                if (shopRes.ok) {
                  const shop = await shopRes.json()
                  if (shop.location === null) {
                    await fetch(apiUrl(`/api/shops/${shopId}`), {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
                      body: JSON.stringify({ location }),
                    })
                    console.log('✓ [useUploader] Updated shop location from scan:', location)
                  }
                }
              } catch (err) {
                console.warn('⚠️ [useUploader] Failed to update shop location:', err)
              }
            }
          }
        } catch {
          // Keep polling on transient errors
        }
      }, POLL_INTERVAL_MS)
    },
    [stopPolling],
  )

  /**
   * Core upload logic: uploads frame blobs for a specific tier using SAS URLs.
   * tier = 'lowres' | 'hd' | 'ultra'
   */
  const uploadFrameBatch = useCallback(
    async (
      frames: PersistedFrame[],
      urls: UploadUrl[],
      tier: 'lowres' | 'hd' | 'ultra',
      _scanSessionId: string,
      startUploaded: number,
    ): Promise<{ uploaded: number; failed: number }> => {
      let uploaded = startUploaded
      let failed = 0
      const total = startUploaded + frames.length

      setProgress({ total, uploaded, failed: 0, percent: Math.round((uploaded / total) * 100) })

      // Build a map from frameIndex to URL info
      const urlByIndex = new Map<number, UploadUrl>()
      for (const u of urls) urlByIndex.set(u.frame_index, u)

      // Use lower concurrency for background HD/ultra uploads
      const concurrency = tier === 'lowres' ? CONCURRENT_UPLOADS : 2

      // Process in batches
      for (let i = 0; i < frames.length; i += concurrency) {
        const batch = frames.slice(i, i + concurrency)
        const promises = batch.map(async (frame) => {
          const urlInfo = urlByIndex.get(frame.frameIndex)
          if (!urlInfo) {
            console.error(`[useUploader] No SAS URL for frame index ${frame.frameIndex}`)
            failed++
            return
          }

          let uploadUrl: string | undefined
          let blob: Blob | undefined

          if (tier === 'lowres') {
            uploadUrl = urlInfo.upload_url
            blob = frame.blob
          } else if (tier === 'hd') {
            uploadUrl = urlInfo.hd_upload_url
            blob = frame.hdBlob
          } else if (tier === 'ultra') {
            uploadUrl = urlInfo.ultra_upload_url
            blob = frame.ultraBlob
          }

          if (!uploadUrl || !blob) {
            // No URL or blob for this tier — skip silently
            uploaded++
            setProgress({ total, uploaded, failed, percent: Math.round(((uploaded + failed) / total) * 100) })
            return
          }

          try {
            await uploadFrame(uploadUrl, blob, MAX_RETRIES)
            uploaded++
          } catch {
            failed++
            console.error(`Failed to upload ${tier} frame ${frame.frameIndex}`)
          }
          setProgress({ total, uploaded, failed, percent: Math.round(((uploaded + failed) / total) * 100) })
        })
        await Promise.all(promises)
      }

      return { uploaded, failed }
    },
    [],
  )

  // ── Fresh upload (from capture) ──
  const upload = useCallback(
    async (
      framePairs: FramePair[],
      shopId: string,
      sectionName: string,
      sectionId?: string,
      location?: { lat: number; lng: number } | null,
      scanSessionId?: string,
      rescanOptions?: { mode: 'rescan'; anchor_frame_index?: number },
      includeUltra?: boolean,
      sectionCategory?: string,
    ) => {
      try {
        setError(null)
        setPhase('creating')
        isUploadingRef.current = true
        abortedRef.current = false

        // Use provided sectionId or generate from name
        const finalSectionId = sectionId || ('sec_' + sectionName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''))

        // Step 1: Create scan (with location if available)
        const newScan = await createScan({
          shop_id: shopId,
          section_id: finalSectionId,
          section_name: sectionName,
          frame_count: framePairs.length,
          location: location || null,
          ...(sectionCategory ? { section_category: sectionCategory } : {}),
          ...(rescanOptions ? { mode: rescanOptions.mode, anchor_frame_index: rescanOptions.anchor_frame_index } : {}),
        })
        setScan(newScan)

        // Update IndexedDB session with server scan ID
        if (scanSessionId) {
          await updateSession(scanSessionId, { serverScanId: newScan.id, status: 'uploading' })
        }

        // Step 2: Get SAS upload URLs (includes standard + HD + optional ultra URLs)
        setPhase('uploading')
        const { urls } = await getUploadUrls(newScan.id, framePairs.length, includeUltra)

        // Step 3: Get frames from IndexedDB if session is available, otherwise use in-memory blobs
        let framesToUpload: PersistedFrame[]
        if (scanSessionId) {
          framesToUpload = await getFramesBySession(scanSessionId)
          // Sort by frameIndex to match URL order
          framesToUpload.sort((a, b) => a.frameIndex - b.frameIndex)
        } else {
          // Fallback: create temporary PersistedFrame objects from in-memory framePairs
          framesToUpload = framePairs.map((fp, idx) => ({
            id: `temp_${idx + 1}`,
            scanSessionId: 'temp',
            frameIndex: idx + 1,
            blob: fp.blob,
            hdBlob: fp.hdBlob,
            ...(fp.ultraBlob ? { ultraBlob: fp.ultraBlob } : {}),
            status: 'pending' as const,
            createdAt: Date.now(),
          }))
        }

        // ── Phase 1: Upload low-res frames (priority — COLMAP needs these) ──
        const lowRes = await uploadFrameBatch(framesToUpload, urls, 'lowres', scanSessionId || 'temp', 0)

        if (lowRes.failed > 0) {
          console.warn(`[useUploader] ${lowRes.failed} low-res frame uploads failed`)
        }

        // Step 4: Mark scan complete → COLMAP starts processing with low-res frames
        setPhase('completing')
        const completed = await markScanComplete(newScan.id)
        setScan(completed)

        // Update IndexedDB session
        if (scanSessionId) {
          await updateSession(scanSessionId, { status: 'completed' })
        }

        // Step 5: Poll for processing completion
        startPolling(newScan.id, shopId, location)

        // ── Phase 2: Background upload HD frames (non-blocking) ──
        console.log('[useUploader] Starting background HD upload...')
        uploadFrameBatch(framesToUpload, urls, 'hd', scanSessionId || 'temp', 0)
          .then(({ failed }) => {
            if (failed > 0) console.warn(`[useUploader] ${failed} HD uploads failed`)
            else console.log('[useUploader] HD upload complete')

            // ── Phase 3: Background upload ultra frames (if applicable) ──
            if (includeUltra) {
              console.log('[useUploader] Starting background 4K upload...')
              return uploadFrameBatch(framesToUpload, urls, 'ultra', scanSessionId || 'temp', 0)
            }
          })
          .then((result) => {
            if (result && result.failed > 0) console.warn(`[useUploader] ${result.failed} ultra uploads failed`)
            else if (result) console.log('[useUploader] 4K upload complete')

            isUploadingRef.current = false

            // Clean up IndexedDB after all tiers uploaded
            if (scanSessionId) {
              deleteSessionAndFrames(scanSessionId).catch(() => {})
            }
          })
          .catch((err) => {
            console.warn('[useUploader] Background upload error:', err)
            isUploadingRef.current = false
          })
      } catch (err) {
        isUploadingRef.current = false
        const msg = err instanceof Error ? err.message : 'Upload failed'
        setError(msg)
        setPhase('error')

        // Mark session as failed but keep frames for retry
        if (scanSessionId) {
          await updateSession(scanSessionId, { status: 'ready' }).catch(() => {})
        }
      }
    },
    [stopPolling, uploadFrameBatch, startPolling],
  )

  // ── Resume interrupted upload ──
  const resume = useCallback(
    async (session: ScanSession) => {
      try {
        setError(null)
        isUploadingRef.current = true
        abortedRef.current = false

        // Get remaining pending frames from IndexedDB
        const pendingFrames = await getPendingFrames(session.id)
        if (pendingFrames.length === 0) {
          setError('No pending frames found to resume')
          setPhase('error')
          isUploadingRef.current = false
          return
        }

        pendingFrames.sort((a, b) => a.frameIndex - b.frameIndex)
        const alreadyUploaded = session.frameCount - pendingFrames.length

        // Check if any frames have ultraBlob (indicates ultra tier scan)
        const hasUltra = pendingFrames.some((f) => f.ultraBlob != null)

        // If we don't have a server scan ID yet, create one
        let serverScanId = session.serverScanId
        if (!serverScanId) {
          setPhase('creating')
          const newScan = await createScan({
            shop_id: session.shopId,
            section_id: session.sectionId,
            section_name: session.sectionName,
            frame_count: session.frameCount,
            location: session.location || null,
          })
          setScan(newScan)
          serverScanId = newScan.id
          await updateSession(session.id, { serverScanId, status: 'uploading' })
        }

        // Request SAS URLs for ALL frames (backend generates for frame_count starting at 1)
        setPhase('uploading')
        const { urls } = await getUploadUrls(serverScanId, session.frameCount, hasUltra)
        await updateSession(session.id, { status: 'uploading' })

        // ── Phase 1: Upload low-res frames (priority) ──
        const lowRes = await uploadFrameBatch(pendingFrames, urls, 'lowres', session.id, alreadyUploaded)

        if (lowRes.failed > 0) {
          console.warn(`[useUploader] Resume: ${lowRes.failed} low-res uploads failed`)
        }

        // Mark scan complete
        setPhase('completing')
        const completed = await markScanComplete(serverScanId)
        setScan(completed)
        await updateSession(session.id, { status: 'completed' })

        // Poll for processing
        startPolling(serverScanId, session.shopId, session.location)

        // ── Phase 2+3: Background HD + ultra upload ──
        uploadFrameBatch(pendingFrames, urls, 'hd', session.id, 0)
          .then(({ failed }) => {
            if (failed > 0) console.warn(`[useUploader] ${failed} HD uploads failed`)
            if (hasUltra) {
              return uploadFrameBatch(pendingFrames, urls, 'ultra', session.id, 0)
            }
          })
          .then((result) => {
            if (result && result.failed > 0) console.warn(`[useUploader] ${result.failed} ultra uploads failed`)
            isUploadingRef.current = false
            deleteSessionAndFrames(session.id).catch(() => {})
          })
          .catch((err) => {
            console.warn('[useUploader] Background upload error:', err)
            isUploadingRef.current = false
          })
      } catch (err) {
        isUploadingRef.current = false
        const msg = err instanceof Error ? err.message : 'Resume upload failed'
        setError(msg)
        setPhase('error')

        // Keep frames for another retry
        await updateSession(session.id, { status: 'ready' }).catch(() => {})
      }
    },
    [uploadFrameBatch, startPolling],
  )

  // Reset state
  const reset = useCallback(() => {
    stopPolling()
    isUploadingRef.current = false
    abortedRef.current = false
    setPhase('idle')
    setProgress({ total: 0, uploaded: 0, failed: 0, percent: 0 })
    setScan(null)
    setError(null)
  }, [stopPolling])

  return { phase, progress, scan, error, upload, resume, reset }
}
